/**
 * Karta produktu — single home for the computed readouts of the product card
 * and list. A product (and a real variant) has piece dimensions and the weight
 * of one piece; pallets, packages and LDM were removed (owner decision
 * 2026-09-30, docs/decisions/2026-09-30-bez-palet-podzial-transportu-po-wartosci.md).
 *
 * Piece geometry reuses `resolveDimensionGeometryMm` (the same plane/depth
 * rule as orders, PZ/WZ and transport). Missing data stays null; nothing is
 * guessed.
 */
import { resolveDimensionGeometryMm } from '../../utils/dimensionConversion';

export type AverageCostUnit = 'pcs' | 'm2' | 'm3';

export interface ProductLogisticsInput {
  length?: number | null;
  width?: number | null;
  thickness?: number | null;
  height?: number | null;
  /** Weight of one piece in kg. */
  grossWeight?: number | null;
  /**
   * „Liczymy tylko w sztukach” (migration 196): the dimensions stay a
   * description; no m² / m³ is derived from them (same seam as the backend).
   */
  countInPiecesOnly?: boolean | null;
}

export type ProductDataGap = 'weight' | 'cost';

const positive = (value: unknown): number | null => {
  const numeric = typeof value === 'number' ? value : Number(value);
  return value !== null && value !== undefined && value !== '' && Number.isFinite(numeric) && numeric > 0
    ? numeric
    : null;
};

export interface PieceMetrics {
  areaM2: number | null;
  volumeM3: number | null;
  piecesPerM2: number | null;
  kgPerM2: number | null;
  weightKg: number | null;
}

export function calculatePieceMetrics(input: ProductLogisticsInput): PieceMetrics {
  const geometry = resolveDimensionGeometryMm({
    length: positive(input.length) ?? undefined,
    width: positive(input.width) ?? undefined,
    height: positive(input.height) ?? undefined,
    thickness: positive(input.thickness) ?? undefined,
    countInPiecesOnly: input.countInPiecesOnly === true,
  });
  const areaM2 = geometry.areaPerPieceM2 && geometry.areaPerPieceM2 > 0 ? geometry.areaPerPieceM2 : null;
  const volumeM3 = geometry.volumePerPieceM3 && geometry.volumePerPieceM3 > 0 ? geometry.volumePerPieceM3 : null;
  const weightKg = positive(input.grossWeight);
  return {
    areaM2,
    volumeM3,
    piecesPerM2: areaM2 ? 1 / areaM2 : null,
    kgPerM2: areaM2 && weightKg ? weightKg / areaM2 : null,
    weightKg,
  };
}

export interface AverageCostConversions {
  perPiece: number | null;
  perM2: number | null;
  perM3: number | null;
}

/** Re-expresses the average cost (PLN net in its unit) in the other units. */
export function convertAverageCost(
  price: number | null | undefined,
  unit: AverageCostUnit | null | undefined,
  input: ProductLogisticsInput,
): AverageCostConversions {
  const empty = { perPiece: null, perM2: null, perM3: null };
  if (price === null || price === undefined || !Number.isFinite(Number(price)) || !unit) return empty;
  const piece = calculatePieceMetrics(input);
  const value = Number(price);
  const perPiece = unit === 'pcs'
    ? value
    : unit === 'm2'
      ? (piece.areaM2 ? value * piece.areaM2 : null)
      : (piece.volumeM3 ? value * piece.volumeM3 : null);
  if (perPiece === null) return { ...empty, [unit === 'm2' ? 'perM2' : 'perM3']: value };
  return {
    perPiece,
    perM2: piece.areaM2 ? perPiece / piece.areaM2 : null,
    perM3: piece.volumeM3 ? perPiece / piece.volumeM3 : null,
  };
}

/**
 * What the list's "Dane" column and the "Niekompletne" filter report: the
 * piece weight and (for viewers who see it) the average cost.
 * `includeCost` is false for a viewer who cannot see the average cost —
 * redacted data is not the same as missing data.
 */
export function getProductDataGaps(
  product: ProductLogisticsInput & { averagePurchasePricePLN?: number | null },
  { includeCost }: { includeCost: boolean },
): ProductDataGap[] {
  const gaps: ProductDataGap[] = [];
  if (!positive(product.grossWeight)) gaps.push('weight');
  if (includeCost && (product.averagePurchasePricePLN === null || product.averagePurchasePricePLN === undefined)) {
    gaps.push('cost');
  }
  return gaps;
}

/**
 * Form values of the logistics set shared by the product card and the variant
 * editor (a real variant owns its dimensions and piece weight — owner decision
 * 2026-09-24, docs/decisions/2026-09-24-wariant-wlasne-dane-logistyczne.md).
 * A new variant is prefilled from its product through this same mapping.
 */
export function toLogisticsFormValues(source: ProductLogisticsInput | null | undefined): ProductLogisticsInput {
  return {
    length: source?.length ?? null,
    width: source?.width ?? null,
    thickness: source?.thickness ?? null,
    // "height" stays editable only where it is already used as a plane side.
    ...(Number(source?.height) > 0 ? { height: source?.height } : {}),
    grossWeight: source?.grossWeight ?? null,
  };
}

/** The card's „Liczymy tylko w sztukach” value (product only; variants inherit). */
export function toCountInPiecesOnlyFormValue(source: { countInPiecesOnly?: boolean | null } | null | undefined): boolean {
  return source?.countInPiecesOnly === true;
}
